export const cookieName = "wf_ab_exp";
export const channelCookieName = "wf_ab_exp_channels";

const data = {
	/**
	 * The date string, used to string-timestamp cookies and to control "versions" of the experimentation. Changing this value will regenerate cookies for users. Written in YYMMDD format.
	 */
	date: "190510",
	/**
	 * An object of four key-value pairs, where the key is one of the four channels (ABCD), and the weight is an integer (0-100) indicating the percentage of the channel. This should always follow the weights defined for this release in the Confluence document.
	 *
	 * @note The total weight for all channels should ALWAYS be 100.
	 */
	channels: {
		A: 80,
		B: 20,
	},
};

const getCookieValue = (channel) =>
	`v_${data.date}_${channel}_${data.channels[channel]}`;

export const getChannelCookieString = (weightsOverride = null) => {
	const c = weightsOverride !== null ? weightsOverride : data.channels;

	let cookieArray = [];
	for (var channel in c) {
		var weight = c[channel];
		cookieArray.push(`${channel}${weight}`);
	}

	return cookieArray.join("_");
};

export const readChannelCookieString = (cookieString) => {
	if (typeof cookieString !== "string" || !cookieString.length) return null;
	const data = {};
	const cookieArray = cookieString.split("_");
	cookieArray.forEach((segment) => {
		const segMatch = segment.match(/^([A-D])(\d+)$/);
		if (segMatch) {
			data[segMatch[1]] = parseInt(segMatch[2]);
		}
	});

	return data;
};

export const isValidChannel = (channel) =>
	["A", "B", "C", "D"].indexOf(channel) >= 0;

export const readCookieString = (cookieString) => {
	const debug = process.env.NODE_ENV !== "production";
	const d = {};

	if (typeof cookieString !== "string") {
		if (debug) {
			console.debug("The AB testing cookie does not exist");
		}
		return null;
	}

	const regex = cookieString.match(/^v_(\d{6})_([A-Z])_(\d+)$/);
	if (regex === null) {
		if (debug) {
			console.debug(
				`The AB testing cookie has an invalid value of ${cookieString}`
			);
		}
		return null;
	}

	d.channel = regex[2];
	d.date = regex[1];
	d.weight = parseInt(regex[3]);

	return d;
};

export const generateCookie = (
	channelOverride = null,
	weightsOverride = null
) => {
	// Pre-render and test asuming experiment A.
	if (
		typeof window.__PRERENDER_INJECTED !== "undefined" ||
		typeof window.__TEST__ !== "undefined"
	) {
		return getCookieValue("A");
	}

	// If an override is given, check if it is a valid channel and return the cookie accordingly.
	if (
		typeof channelOverride === "string" &&
		isValidChannel(channelOverride) &&
		data.channels[channelOverride] !== undefined
	) {
		return getCookieValue(channelOverride);
	}

	const c = weightsOverride === null ? data.channels : weightsOverride;
	let chosenChannel = null;

	while (chosenChannel === null) {
		let i = Math.ceil(Math.random() * 100);
		for (let channel in c) {
			let weight = c[channel];
			if (i <= weight) {
				chosenChannel = channel;
				break;
			} else {
				i -= weight;
			}
		}
	}

	return getCookieValue(chosenChannel);
};

export const isMatchingWeight = (cookieData) => {
	const stub = {
		channel: "B",
		date: "190510",
		weight: 20,
	};

	cookieData = navigator.cookieEnabled ? cookieData : stub;
	if (typeof cookieData !== "object") return false;
	if (!cookieData.channel || !cookieData.weight) return false;

	const cookieWeight = cookieData.weight;
	const actualWeight = data.channels[cookieData.channel];

	return cookieWeight === actualWeight;
};

export const updateWeight = (cookieData, oldChannels = null) => {
	if (oldChannels === null) {
		const newCookieString = generateCookie();
		return readCookieString(newCookieString);
	}

	const cookieChannel = cookieData.channel;
	const actualWeight = data.channels[cookieChannel];

	const increments = {};

	// Get a combination of both old and new channels, to cover everything we need for migration.
	let oldChannelKeys = Object.keys(oldChannels);
	let newChannelKeys = Object.keys(data.channels).filter(
		(channel) => oldChannelKeys.indexOf(channel) < 0
	);

	const combinedKeys = oldChannelKeys.concat(newChannelKeys);

	combinedKeys.forEach((channel) => {
		let increment;
		if (
			!data.channels[channel] ||
			oldChannels[channel] >= data.channels[channel]
		) {
			increment = 0;
		} else {
			increment = oldChannels[channel]
				? data.channels[channel] - oldChannels[channel]
				: data.channels[channel];
		}
		increments[channel] = increment;
	});

	if (increments[cookieChannel] > 0) {
		// The new weight is higher than the old one, the user stays in this channel. Update the weight and continue.
		cookieData.weight = actualWeight;
		return cookieData;
	}

	const weightsOverride = {};
	for (var channel in data.channels) {
		let incrementPercentage;
		if (!oldChannels[cookieChannel]) {
			incrementPercentage = 1;
		} else {
			if (!data.channels[cookieChannel]) {
				incrementPercentage = 0;
			} else {
				incrementPercentage =
					data.channels[cookieChannel] / oldChannels[cookieChannel];
			}
		}

		const otherIncrementsSum = Object.keys(increments)
			.filter((c) => c !== cookieChannel)
			.reduce((sum, c) => (sum += increments[c]), 0);

		if (channel === cookieChannel) {
			weightsOverride[channel] = incrementPercentage;
		} else {
			weightsOverride[channel] =
				(1 - incrementPercentage) *
				(increments[channel] / otherIncrementsSum);
		}

		weightsOverride[channel] = Math.round(weightsOverride[channel] * 100);
	}

	const newCookieString = generateCookie(null, weightsOverride);
	return readCookieString(newCookieString);
};

export default data;
